/**
 * Service pour gérer la persistance des datasets Big Data
 */

import { authFetch } from '@/utils/api-headers';
import { API_URL } from '@/lib/config';
import { debugLog } from '../utils/debug-logger';
import logger from '../utils/logger';

export interface BigDataDataset {
  id: string;
  user_id: string;
  file_name: string;
  file_size: number;
  original_size?: number;
  s3_key: string;
  s3_bucket: string;
  job_id: string;
  status: 'processing' | 'completed' | 'error';
  progress: number;
  table_name?: string;
  row_count?: number;
  column_count?: number;
  columns?: any;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  indexes_created?: number;
  parquet_path?: string;
  compression_ratio?: number;
}

export interface DatasetMetadata {
  rowCount: number;
  columnCount: number;
  columns: Array<{
    name: string;
    type: string;
    nullable: boolean;
  }>;
}

export class BigDataDatasetService {
  /**
   * Parse les colonnes si elles sont au format JSON string
   */
  private static parseColumns(columns: any): any {
    debugLog('[🔍 DatasetService] parseColumns - INPUT TYPE:', typeof columns);
    debugLog('[🔍 DatasetService] parseColumns - IS ARRAY:', Array.isArray(columns));
    debugLog('[🔍 DatasetService] parseColumns - VALUE:', columns);
    
    if (!columns) {
      logger.warn('[⚠️ DatasetService] parseColumns - columns is NULL/UNDEFINED');
      return null;
    }
    
    // Si c'est déjà un array, on le retourne tel quel
    if (Array.isArray(columns)) {
      debugLog('[✅ DatasetService] parseColumns - Already an array, length:', columns.length);
      return columns;
    }
    
    // Si c'est une string JSON, on la parse
    if (typeof columns === 'string') {
      debugLog('[🔄 DatasetService] parseColumns - Parsing JSON string...');
      try {
        const parsed = JSON.parse(columns);
        debugLog('[✅ DatasetService] parseColumns - Parsed successfully, isArray:', Array.isArray(parsed));
        return Array.isArray(parsed) ? parsed : null;
      } catch (e) {
        logger.error('[❌ DatasetService] Failed to parse columns JSON:', e);
        logger.error('[❌ DatasetService] Columns value was:', columns);
        return null;
      }
    }
    
    // Si c'est un objet (pas un array), peut-être qu'il y a une erreur de structure
    if (typeof columns === 'object') {
      logger.warn('[⚠️ DatasetService] Columns is an object but not an array:', columns);
      logger.warn('[⚠️ DatasetService] Object keys:', Object.keys(columns));
      return null;
    }
    
    logger.warn('[⚠️ DatasetService] parseColumns - Unknown type, returning null');
    return null;
  }

  /**
   * Normaliser un dataset reçu de l'API
   */
  private static normalizeDataset(dataset: any): BigDataDataset {
    debugLog('[🔧 DatasetService] normalizeDataset - Dataset ID:', dataset.id);
    debugLog('[🔧 DatasetService] normalizeDataset - Dataset name:', dataset.file_name);
    debugLog('[🔧 DatasetService] normalizeDataset - Raw columns type:', typeof dataset.columns);
    debugLog('[🔧 DatasetService] normalizeDataset - Raw columns:', dataset.columns);
    
    const parsedColumns = this.parseColumns(dataset.columns);
    
    debugLog('[🔧 DatasetService] normalizeDataset - Parsed columns:', parsedColumns);
    debugLog('[🔧 DatasetService] normalizeDataset - Parsed columns length:', parsedColumns?.length || 0);
    
    const normalized = {
      ...dataset,
      columns: parsedColumns,
    };
    
    debugLog('[✅ DatasetService] normalizeDataset - RESULT:', {
      id: normalized.id,
      file_name: normalized.file_name,
      hasColumns: !!normalized.columns,
      columnsCount: normalized.columns?.length || 0,
      status: normalized.status
    });
    
    return normalized;
  }

  /**
   * Créer un nouveau dataset après upload
   */
  static async createDataset(data: {
    userId: string;
    fileName: string;
    fileSize: number;
    s3Key: string;
    s3Bucket: string;
    jobId: string;
  }): Promise<BigDataDataset> {
    debugLog('[DatasetService] Creating dataset (API):', data.fileName);

    const resp = await authFetch(`${API_URL}/api/big-data-server/datasets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!resp.ok) {
      const txt = await resp.text();
      throw new Error(`Failed to create dataset: ${txt}`);
    }

    const json = await resp.json();
    debugLog('[DatasetService] Dataset created:', json.dataset?.id);
    return json.dataset as BigDataDataset;
  }

  /**
   * Mettre à jour le statut d'un dataset
   */
  static async updateDatasetStatus(
    jobId: string,
    status: 'processing' | 'completed' | 'error',
    progress: number,
    metadata?: Partial<BigDataDataset>
  ): Promise<void> {
    debugLog(`[DatasetService] Updating dataset ${jobId}: ${status} (${progress}%)`);

    const updateData: any = {
      status,
      progress,
      updated_at: new Date().toISOString(),
    };

    if (status === 'completed') {
      updateData.completed_at = new Date().toISOString();
    }

    if (metadata) {
      Object.assign(updateData, metadata);
    }

    // ✅ FIX: Utiliser l'API backend au lieu de Supabase direct
    const resp = await authFetch(`${API_URL}/api/big-data-server/datasets/update/${jobId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updateData),
    });

    if (!resp.ok) {
      const txt = await resp.text();
      logger.error('[DatasetService] Error updating dataset:', txt);
      throw new Error(`Failed to update dataset: ${txt}`);
    }
  }

  /**
   * Récupérer tous les datasets d'un utilisateur
   */
  static async getUserDatasets(userId: string): Promise<BigDataDataset[]> {
    debugLog('[📡 DatasetService] getUserDatasets - Fetching for user:', userId);

    const resp = await authFetch(
      `${API_URL}/api/big-data-server/datasets?userId=${encodeURIComponent(userId)}`,
      { method: 'GET' }
    );

    if (!resp.ok) {
      const txt = await resp.text();
      logger.error('[❌ DatasetService] getUserDatasets - API error:', txt);
      throw new Error(`Failed to fetch datasets: ${txt}`);
    }

    const json = await resp.json();
    debugLog(`[📦 DatasetService] getUserDatasets - RAW API Response:`, json);
    debugLog(`[📊 DatasetService] getUserDatasets - Found ${json.datasets?.length || 0} datasets`);
    
    // Log premier dataset brut pour debug
    if (json.datasets && json.datasets.length > 0) {
      debugLog('[🔍 DatasetService] getUserDatasets - First dataset RAW:', json.datasets[0]);
      debugLog('[🔍 DatasetService] getUserDatasets - First dataset columns type:', typeof json.datasets[0].columns);
      debugLog('[🔍 DatasetService] getUserDatasets - First dataset columns:', json.datasets[0].columns);
    }
    
    // ✅ Parser les colonnes pour chaque dataset
    const datasets = (json.datasets || []).map((d: any, index: number) => {
      debugLog(`[🔄 DatasetService] Processing dataset ${index + 1}/${json.datasets.length}:`, d.file_name);
      return this.normalizeDataset(d);
    });
    
    debugLog('[✅ DatasetService] getUserDatasets - Normalized datasets count:', datasets.length);
    return datasets;
  }

  /**
   * Récupérer un dataset par job_id
   */
  static async getDatasetByJobId(jobId: string): Promise<BigDataDataset | null> {
    debugLog('[📡 DatasetService] getDatasetByJobId - Fetching job_id:', jobId);

    const resp = await authFetch(
      `${API_URL}/api/big-data-server/datasets?jobId=${encodeURIComponent(jobId)}`,
      { method: 'GET' }
    );

    if (!resp.ok) {
      const txt = await resp.text();
      logger.error('[❌ DatasetService] getDatasetByJobId - API error:', txt);
      throw new Error(`Failed to fetch dataset: ${txt}`);
    }

    const json = await resp.json();
    debugLog('[📦 DatasetService] getDatasetByJobId - RAW Response:', json);
    
    if (json.datasets && json.datasets.length > 0) {
      debugLog('[🔍 DatasetService] getDatasetByJobId - Found dataset:', json.datasets[0].file_name);
      const normalized = this.normalizeDataset(json.datasets[0]);
      debugLog('[✅ DatasetService] getDatasetByJobId - Normalized:', normalized);
      return normalized;
    }
    
    logger.warn('[⚠️ DatasetService] getDatasetByJobId - No dataset found');
    return null;
  }

  /**
   * Récupérer un dataset par ID
   */
  static async getDatasetById(datasetId: string): Promise<BigDataDataset | null> {
    debugLog('[📡 DatasetService] getDatasetById - Fetching ID:', datasetId);

    // ✅ FIX: Utiliser l'API backend au lieu de Supabase direct
    const resp = await authFetch(
      `${API_URL}/api/big-data-server/datasets/${datasetId}`,
      { method: 'GET' }
    );

    if (!resp.ok) {
      if (resp.status === 404) {
        logger.warn('[⚠️ DatasetService] getDatasetById - Dataset not found (404)');
        return null;
      }
      const txt = await resp.text();
      logger.error('[❌ DatasetService] getDatasetById - API error:', txt);
      throw new Error(`Failed to fetch dataset: ${txt}`);
    }

    const json = await resp.json();
    debugLog('[📦 DatasetService] getDatasetById - RAW Response:', json);
    
    if (json.dataset) {
      debugLog('[🔍 DatasetService] getDatasetById - Found dataset:', json.dataset.file_name);
      const normalized = this.normalizeDataset(json.dataset);
      debugLog('[✅ DatasetService] getDatasetById - Normalized:', normalized);
      return normalized;
    }
    
    logger.warn('[⚠️ DatasetService] getDatasetById - No dataset in response');
    return null;
  }

  /**
   * Supprimer un dataset
   */
  static async deleteDataset(datasetId: string, userId: string): Promise<void> {
    debugLog('[DatasetService] Deleting dataset:', datasetId);

    const resp = await authFetch(
      `${API_URL}/api/big-data-server/datasets/${datasetId}?userId=${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );

    if (!resp.ok) {
      const txt = await resp.text();
      throw new Error(`Failed to delete dataset: ${txt}`);
    }

    debugLog('[DatasetService] Dataset deleted successfully');
  }

  /**
   * Vérifier si un dataset est prêt
   */
  static async isDatasetReady(jobId: string): Promise<boolean> {
    const dataset = await this.getDatasetByJobId(jobId);
    return dataset?.status === 'completed';
  }

  /**
   * Obtenir les statistiques d'un utilisateur
   */
  static async getUserStats(userId: string): Promise<{
    totalDatasets: number;
    totalSize: number;
    completedDatasets: number;
    processingDatasets: number;
  }> {
    const datasets = await this.getUserDatasets(userId);

    return {
      totalDatasets: datasets.length,
      totalSize: datasets.reduce((sum, d) => sum + d.file_size, 0),
      completedDatasets: datasets.filter((d) => d.status === 'completed').length,
      processingDatasets: datasets.filter((d) => d.status === 'processing').length,
    };
  }
}

export default BigDataDatasetService;

